import { BaseDisplayer } from './BaseDisplayer';
import { ValueType, TEDParseNumberMode } from '../FormatterTypes';
import {
  makeNumberFormatString,
  numberMultiple,
  iLimit,
  stringOfChars,
  nextFormatItem,
} from '../Utils/Other';
import {
  formatterStrParamDef,
  formatterIntParamDef,
  formatterBoolParamDef,
} from '../Utils/ParamUtils';
import {
  asCurrencyDef,
  asNumberDef,
  tryAsCurrency,
  asNumber,
  asInt,
  intToStrEx2,
  currToStrEx2,
  floatToStrEx2,
} from '../Utils/ValueUtils';

abstract class TEDCustomNumberDisplayer extends BaseDisplayer {
  abstract mode(): TEDParseNumberMode;

  makeFormatString(params: string, displayText: boolean): string | null {
    let formatString = formatterStrParamDef(
      'Format',
      params,
      this.defaultParams(),
      ''
    );

    let defaultParams = this.defaultParams();
    let dec = 0;
    if (formatString.length === 0) {
      if (
        [
          TEDParseNumberMode.pnmCurrency,
          TEDParseNumberMode.pnmPercent,
          TEDParseNumberMode.pnmDegrees,
        ].includes(this.mode()) ||
        (displayText && this.mode() === TEDParseNumberMode.pnmFloat)
      ) {
        dec = formatterIntParamDef('Decimals', params, defaultParams, 2);
      } else {
        dec = 10;
      }

      const isUnsigned = formatterBoolParamDef(
        'Unsigned',
        params,
        defaultParams
      );

      const isPercent =
        formatterStrParamDef('Base', params, defaultParams) === 'P';

      const forceDecimals = formatterBoolParamDef(
        'ForceDecimals',
        params,
        defaultParams
      );

      switch (this.mode()) {
        case TEDParseNumberMode.pnmInt64: // -19u
          formatString = makeNumberFormatString(
            !isUnsigned,
            16, // Number.MAX_SAFE_INTEGER är 16 digits
            0,
            isPercent
          );
          break;
        case TEDParseNumberMode.pnmInteger: // -10u
          formatString = makeNumberFormatString(!isUnsigned, 10, 0, isPercent);
          break;
        case TEDParseNumberMode.pnmCurrency: // -15u.2f
          if (numberMultiple(this.mode(), params, defaultParams) === 1) {
            formatString = makeNumberFormatString(
              !isUnsigned,
              15,
              iLimit(dec, 0, 4),
              false,
              true
            );

            if (!displayText) {
              formatString += stringOfChars('g', 4 - iLimit(dec, 0, 4));
            }
          } else {
            formatString = makeNumberFormatString(
              !isUnsigned,
              15,
              iLimit(dec, 0, 10),
              isPercent,
              displayText && forceDecimals
            );
          }
          break;
        case TEDParseNumberMode.pnmFloat: // -15u.2g
          formatString = makeNumberFormatString(
            !isUnsigned,
            15,
            iLimit(dec, 0, 10),
            isPercent,
            displayText && forceDecimals
          );
          break;
        case TEDParseNumberMode.pnmDegrees: // -5u.2f%
          formatString = makeNumberFormatString(
            !isUnsigned,
            5,
            iLimit(dec, 0, 4),
            false,
            false,
            true
          );
          break;
        case TEDParseNumberMode.pnmPercent: // -5u.2f%
          formatString = makeNumberFormatString(
            !isUnsigned,
            10,
            iLimit(dec, 0, 4),
            true,
            true
          );
          break;
        default:
          formatString = '';
      }
    } else {
      return formatString;
    }

    return formatString.length > 0 ? formatString : null;
  }

  defaultParams(): string {
    switch (this.mode()) {
      case TEDParseNumberMode.pnmInteger:
      case TEDParseNumberMode.pnmInt64:
        return 'ThousandSep="";Unsigned="N";ReverseSign="N";DisplayZeroAsBlank="N";Format=""';
      case TEDParseNumberMode.pnmPercent:
        return 'Decimals="";DecimalSep=",";ThousandSep=" ";Unsigned="N";ReverseSign="N";DisplayZeroAsBlank="N";Format=""';
      case TEDParseNumberMode.pnmDegrees:
        return 'Decimals="";DecimalSep=",";ThousandSep=" ";Unsigned="N";ReverseSign="N";DisplayZeroAsBlank="N";Format=""';
      case TEDParseNumberMode.pnmFloat:
        return 'Decimals="";ForceDecimals="N";DecimalSep=",";ThousandSep=" ";Base="1";Unsigned="N";ReverseSign="N";DisplayZeroAsBlank="N";Format=""';
      case TEDParseNumberMode.pnmCurrency:
        return 'Decimals="";DecimalSep=",";ThousandSep=" ";Base="1";Unsigned="N";ReverseSign="N";DisplayZeroAsBlank="N";ForceDecimals="N";Format=""';
      default:
        return 'Decimals="";DecimalSep=",";ThousandSep=" ";Base="1";Unsigned="N";ReverseSign="N";DisplayZeroAsBlank="N";Format=""';
    }
  }

  display(
    value: ValueType,
    displayText: boolean = false,
    params: string = ''
  ): string {
    const defaultParams = this.defaultParams();

    const showValue = (): boolean => {
      if (formatterBoolParamDef('DisplayZeroAsBlank', params, defaultParams)) {
        switch (this.mode()) {
          case TEDParseNumberMode.pnmInt64:
          case TEDParseNumberMode.pnmInteger: {
            const n = Number(value);
            return Number.isInteger(n) && n !== 0; // lite oklart om detta stämmer överens med delphi
          }
          case TEDParseNumberMode.pnmCurrency:
            return asCurrencyDef(value) !== 0; // Fipplat här!
          default:
            return asNumberDef(value) !== 0;
        }
      }

      if (this.mode() === TEDParseNumberMode.pnmCurrency) {
        return tryAsCurrency(value) !== null;
      }
      return asNumber(value) !== null;
    };

    // BEGIN DISPLAY
    let result = '';
    let formatString = this.makeFormatString(params, displayText);

    if (showValue() && formatString) {
      let dsep = formatterStrParamDef('DecimalSep', params, defaultParams);
      const tsep = displayText
        ? formatterStrParamDef('ThousandSep', params, defaultParams)
        : '';

      let sign = 0;
      let pSign = 0;
      let decSep = false;
      let intMin = 0;
      let intMax = 0;
      let decMin = 0;
      let decMax = 0;
      let neg = false;
      let tmpstr = '';
      let tmpInt;
      let tmpCurr;
      let tmpext;

      let startIndex = 0;

      while (startIndex < formatString.length) {
        // Cykla igenom formatsträngen och hitta alla speciella formattecken
        const {
          result: ftResult,
          format: fc,
          count: fcnt,
          nextIndex,
        } = nextFormatItem(formatString, startIndex);
        startIndex = nextIndex;

        if (!ftResult) {
          break;
        }

        switch (fc) {
          case '+':
          case '-':
            if (fcnt === 1 && sign === 0) {
              sign = 1;

              if (fc === '+') {
                sign += 2;
              }
              if (intMax > 1) {
                sign += 4;
              }
            }
            break;
          case '%':
            if (fcnt === 1 && pSign === 0) {
              pSign = 1;
              if (intMax > 1) {
                pSign += 4;
              }
              if (sign !== 0) {
                pSign += 8;
              }
            }
            break;
          case '.':
          case ',':
            if (dsep && fcnt === 1 && intMax > 1 && decMax === 0 && !decSep) {
              decSep = true;
            }
            break;
          case 'u':
          case 'U':
          case 'i':
          case 'I':
            if (decMax === 0 && !decSep) {
              if (fc === 'i' || fc === 'I') {
                intMin += fcnt;
              }
              intMax += fcnt;
            }
            break;
          case 'f':
          case 'F':
          case 'g':
          case 'G':
            if (intMax > 1) {
              if (['f', 'F'].includes(fc)) {
                decMin += fcnt;
              }
              decMax += fcnt;
            }
            break;
        }
      }

      intMin = intMin < 1 ? 1 : intMin;
      intMax = intMax < intMin ? intMin : intMax;
      if (!decSep || decMax < 1) {
        dsep = '';
      }

      switch (this.mode()) {
        case TEDParseNumberMode.pnmInt64:
        case TEDParseNumberMode.pnmInteger:
          tmpInt = asInt(value);
          if (formatterBoolParamDef('ReverseSign', params, defaultParams)) {
            tmpInt = -tmpInt;
          }
          neg = tmpInt < 0;
          if (neg) {
            tmpInt = -tmpInt;
          }
          result = intToStrEx2(tmpInt, intMin, intMax, tsep);
          break;
        case TEDParseNumberMode.pnmCurrency: {
          if (typeof value === 'string' && value === '') {
            return '';
          }
          tmpCurr =
            asCurrencyDef(value) *
            numberMultiple(this.mode(), params, defaultParams);
          if (formatterBoolParamDef('ReverseSign', params, defaultParams)) {
            tmpCurr = -tmpCurr;
          }
          neg = tmpCurr < 0;
          if (neg) {
            tmpCurr = -tmpCurr;
          }

          let result2 = currToStrEx2(
            tmpCurr,
            intMin,
            intMax,
            decMin,
            decMax,
            -1,
            tsep,
            dsep
          );

          result = result2.replace('.', ',');
          break;
        }
        default:
          tmpext =
            asNumberDef(value) *
            numberMultiple(this.mode(), params, defaultParams);

          if (formatterBoolParamDef('ReverseSign', params, defaultParams)) {
            tmpext = -tmpext;
          }

          neg = tmpext < 0;
          if (neg) {
            tmpext = -tmpext;
          }

          result = floatToStrEx2(
            tmpext,
            intMin,
            intMax,
            decMin,
            decMax,
            -1,
            tsep,
            dsep
          );
      }

      let prefix = '';
      let suffix = '';
      if (sign !== 0) {
        if (neg) {
          tmpstr = '-';
        } else if ((sign & 2) !== 0) {
          tmpstr = '+';
        } else {
          tmpstr = '';
        }

        if ((sign & 4) === 0) {
          prefix = tmpstr;
        } else {
          suffix = tmpstr;
        }
      }

      if (pSign !== 0 && displayText) {
        tmpstr = '%';
        if ((pSign & 4) === 0) {
          if ((pSign & 8) === 0) {
            prefix = prefix + tmpstr;
          } else {
            prefix = tmpstr + prefix;
          }
        } else {
          if ((pSign & 8) === 0) {
            suffix = suffix + tmpstr;
          } else {
            suffix = tmpstr + suffix;
          }
        }
      }

      if (prefix !== '' || suffix !== '') {
        return prefix + result + suffix;
      } else {
        return result;
      }
    } else {
      // End ShowValue && ...
      return '';
    }
  }

  /// Helpers
  protected decimalCount(params: string, displayText: boolean) {
    const parseModesWithDecimals = [
      TEDParseNumberMode.pnmCurrency,
      TEDParseNumberMode.pnmPercent,
      TEDParseNumberMode.pnmDegrees,
    ];

    if (
      parseModesWithDecimals.some(x => x === this.mode()) ||
      (displayText && this.mode() === TEDParseNumberMode.pnmFloat)
    ) {
      return formatterIntParamDef('Decimals', params, this.defaultParams(), 2);
    } else {
      return 10;
    }
  }

  protected shouldInsertThousandSep(index: number, length: number): boolean {
    return index !== length - 1 && (length - 1 - index) % 3 === 0;
  }
}

export { TEDCustomNumberDisplayer };
